// Vue Router has no default export
import * as VueRouter from "vue-router";

import { RouteRecordRaw } from "vue-router";
import { makeDefaultRouteSet } from "@/router/Factory";
import { FeatureConfig } from "@/types";
import { checkAuthenticate } from "@/router/Authenticator";
import defaultRoutes from "@/router/defaultRoutes";
import { store } from "@/store";
// useFullscreen import 제거 - 컴포넌트 컨텍스트 밖에서 에러 발생 가능성

const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  routes: defaultRoutes,
});

// 로그인 체크
if (!import.meta.env.VITE_IGNORE_LOGIN) {
  router.beforeEach(checkAuthenticate);
}

// 사이트 선택 체크
router.beforeEach((to, from, next) => {
  // select-site 페이지는 항상 접근 가능
  if (to.path === "/select-site") {
    return next();
  }

  // 로그인 페이지는 항상 접근 가능
  if (to.path === "/login") {
    return next();
  }

  // 🔴 새로고침 시 현재 페이지 유지 (라우터 가드에서 이미 처리됨)

  // 사이트가 선택되지 않은 경우 select-site로 리다이렉트
  const selectedSite = localStorage.getItem("selectedSite");
  if (!selectedSite) {
    // 🔴 인증된 사용자는 사이트가 선택되지 않아도 해당 페이지에 머물러 있도록 허용
    const isAuthenticated = store.getters.isAuthenticated;
    if (isAuthenticated) {
      console.log("사이트가 선택되지 않았지만 인증된 사용자이므로 해당 페이지 유지");
      return next();
    } else {
      // 🔴 로그아웃 후에는 로그인 페이지로 이동
      const hasLoginSession = localStorage.getItem("login-session");
      if (!hasLoginSession) {
        console.log("로그인 세션이 없음, 로그인 페이지로 이동");
        return next("/login");
      }
      console.log("사이트가 선택되지 않음, select-site로 리다이렉트");
      return next("/select-site");
    }
  }

  // 🔴 로그인 페이지로 이동하는 경우 사이트 선택 체크 건너뛰기
  if (to.path === "/login") {
    return next();
  }

  next();
});

// 현재 메뉴 설정
router.beforeEach((to, from, next) => {
  // 로그아웃 시 부드러운 전환을 위한 설정
  if (from.path !== "/login" && to.path === "/login") {
    // 로그인 페이지로 이동 시 즉시 레이아웃 변경
    document.body.classList.add("page-transition", "login-transition");

    // 기존 레이아웃 컴포넌트들을 즉시 숨김
    const mainLayout = document.getElementById("main-layout");
    if (mainLayout) {
      mainLayout.style.opacity = "0";
      mainLayout.style.transform = "scale(0.95)";
      mainLayout.style.transition = "all 0.2s ease-out";
    }
  }

  // store가 초기화되지 않은 경우 메뉴 설정 건너뛰기
  if (!store || !store.getters || !store.commit) {
    console.warn("Store가 초기화되지 않아 메뉴 설정을 건너뜁니다.");
    return next();
  }

  // Dashboard 페이지 처리
  if (to.name === "Dashboard" || to.name === "Inner Tab Dashboard") {
    const menuList = store.getters.headerMenu;
    if (menuList && menuList.length > 0) {
      const focusedHeaderId = localStorage.getItem("focusedHeaderId");
      store.commit("currentSelectedMenu", focusedHeaderId ?? menuList[0].id);
    }
  } else {
    // 현재 경로에 맞는 메뉴를 자동으로 찾아서 설정
    const currentPath = to.path;
    const menuData = store.getters.menu || [];

    const isAutoMenuNavigation = localStorage.getItem("autoMenuNavigation") === "true";

    if (isAutoMenuNavigation) {
      localStorage.removeItem("autoMenuNavigation");
    }

    // 1. 정확한 경로 매칭으로 메뉴 찾기
    let matchingMenu = menuData.find((menu: any) => {
      const menuAddress = menu.menuAddress || menu.menuUrl || menu.menuPath || "";
      return menuAddress === currentPath;
    });

    // 2. 부분 경로 매칭으로 메뉴 찾기 (정확한 매칭이 없는 경우)
    if (!matchingMenu) {
      matchingMenu = menuData.find((menu: any) => {
        const menuAddress = menu.menuAddress || menu.menuUrl || menu.menuPath || "";
        return currentPath.startsWith(menuAddress) && menuAddress !== "/";
      });
    }

    // 3. 메뉴 이름 기반 매칭 (경로 매칭이 없는 경우)
    if (!matchingMenu) {
      const pathSegments = currentPath.split("/").filter(Boolean);
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        matchingMenu = menuData.find((menu: any) => {
          const menuName = (menu.menuName || menu.menuNm || "").toLowerCase();
          return menuName.includes(lastSegment.toLowerCase()) || lastSegment.toLowerCase().includes(menuName);
        });
      }
    }

    if (matchingMenu) {
      // 레벨 1 메뉴인 경우 직접 설정
      if (matchingMenu.menuLvl === 1 || matchingMenu.menuLevel === 1) {
        store.commit("currentSelectedMenu", matchingMenu.menuId || matchingMenu.id);
        store.commit("currentSelectedSubMenu", null);
      }
      // 레벨 2 메뉴인 경우 상위 메뉴 설정
      else if (matchingMenu.menuLvl === 2 || matchingMenu.menuLevel === 2) {
        const upperMenuId = matchingMenu.upperMenuId || matchingMenu.parentMenuId || matchingMenu.parentId;
        if (upperMenuId) {
          store.commit("currentSelectedMenu", upperMenuId);
          store.commit("currentSelectedSubMenu", matchingMenu.menuId || matchingMenu.id);
        }
      }

      // localStorage에 focusedHeaderId 저장
      const selectedMenuId =
        matchingMenu.menuLvl === 1
          ? matchingMenu.menuId || matchingMenu.id
          : matchingMenu.upperMenuId || matchingMenu.parentMenuId || matchingMenu.parentId;

      if (selectedMenuId) {
        localStorage.setItem("focusedHeaderId", selectedMenuId);
      }
    } else {
      // 매칭되는 메뉴가 없는 경우 기존 로직 사용
      let focusedHeaderId = localStorage.getItem("focusedHeaderId");

      // Auto navigation 중이면 현재 menu state 유지
      if (isAutoMenuNavigation && focusedHeaderId) {
        store.commit("currentSelectedMenu", focusedHeaderId);
        // Submenu state도 유지
        const currentSubMenu = store.getters.currentSelectedSubMenu;
        if (currentSubMenu) {
          store.commit("currentSelectedSubMenu", currentSubMenu);
        }
      } else {
        store.commit("currentMenu", to.meta?.currentMenu ?? "");
        store.commit("currentSelectedMenu", focusedHeaderId ? focusedHeaderId : to.meta?.currentMenu ?? "");
      }
    }
  }

  // 뒤로가기/앞으로가기 감지 및 메뉴 상태 동기화
  if (from.path !== to.path) {
    // 현재 경로에 맞는 메뉴 상태 복원
    setTimeout(() => {
      try {
        const currentPath = to.path;
        const menuData = store.getters.menu || [];

        // 현재 경로와 일치하는 메뉴 찾기
        const currentMenu = menuData.find((menu: any) => {
          const menuAddress = menu.menuAddress || menu.menuUrl || menu.menuPath || "";
          return menuAddress === currentPath;
        });

        if (currentMenu) {
          // 레벨 1 메뉴인 경우
          if (currentMenu.menuLvl === 1 || currentMenu.menuLevel === 1) {
            store.commit("currentSelectedMenu", currentMenu.menuId || currentMenu.id);
            store.commit("currentSelectedSubMenu", null);
          }
          // 레벨 2 메뉴인 경우
          else if (currentMenu.menuLvl === 2 || currentMenu.menuLevel === 2) {
            const upperMenuId = currentMenu.upperMenuId || currentMenu.parentMenuId || currentMenu.parentId;
            if (upperMenuId) {
              store.commit("currentSelectedMenu", upperMenuId);
              store.commit("currentSelectedSubMenu", currentMenu.menuId || currentMenu.id);
            }
          }
        }
      } catch (error) {
        console.error("메뉴 상태 동기화 중 오류 발생:", error);
      }
    }, 100); // 약간의 지연을 두어 DOM 업데이트 후 실행
  }

  next();
});

router.afterEach(to => {
  // 페이지 전환 완료 후 클래스 제거
  if (to.path === "/login") {
    setTimeout(() => {
      document.body.classList.remove("page-transition", "login-transition");

      // 기존 레이아웃 컴포넌트들을 완전히 숨김
      const mainLayout = document.getElementById("main-layout");
      if (mainLayout) {
        mainLayout.style.display = "none";
      }
    }, 200);
  }

  if (localStorage.getItem("autoMenuNavigation") === "true") {
    localStorage.removeItem("autoMenuNavigation");
  }

  // 풀스크린 설정 - 안전하게 처리
  try {
    // useFullscreen 훅 에러 시 무시 (컴포넌트 컨텍스트 밖에서 호출될 수 있음)
    // 대신 직접 풀스크린 API를 사용하여 더 안전하게 처리
    if (to?.meta?.down || to?.meta?.fullscreen) {
      console.log(to.name, "full");
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  } catch (error) {
    // useFullscreen 훅 에러 시 무시 (컴포넌트 컨텍스트 밖에서 호출될 수 있음)
    console.warn("풀스크린 처리 에러 (무시됨):", error);
  }
});

export const addRoutesFromConfig = (configs: FeatureConfig[]) => {
  addRoutes(configs.map(m => makeDefaultRouteSet(m)).reduce((acc, cur) => [...acc, ...cur], []));
};

const addRoutes = (routes: RouteRecordRaw[]) => {
  routes.forEach(r => {
    router.addRoute(r);
  });
};

export default router;

// 브라우저 뒤로가기/앞으로가기 이벤트 감지 - DOM 로드 완료 후 등록
document.addEventListener("DOMContentLoaded", () => {
  window.addEventListener("popstate", () => {
    // store가 초기화되지 않은 경우 처리하지 않음
    if (!store || !store.getters || !store.commit) {
      console.warn("Store가 초기화되지 않아 popstate 이벤트를 처리할 수 없습니다.");
      return;
    }

    try {
      // 현재 경로에 맞는 메뉴 상태 복원
      const currentPath = window.location.pathname;
      const menuData = store.getters.menu || [];

      // 현재 경로와 일치하는 메뉴 찾기
      const currentMenu = menuData.find((menu: any) => {
        const menuAddress = menu.menuAddress || menu.menuUrl || menu.menuPath || "";
        return menuAddress === currentPath;
      });

      if (currentMenu) {
        // 레벨 1 메뉴인 경우
        if (currentMenu.menuLvl === 1 || currentMenu.menuLevel === 1) {
          store.commit("currentSelectedMenu", currentMenu.menuId || currentMenu.id);
          store.commit("currentSelectedSubMenu", null);
        }
        // 레벨 2 메뉴인 경우
        else if (currentMenu.menuLvl === 2 || currentMenu.menuLevel === 2) {
          const upperMenuId = currentMenu.upperMenuId || currentMenu.parentMenuId || currentMenu.parentId;
          if (upperMenuId) {
            store.commit("currentSelectedMenu", upperMenuId);
            store.commit("currentSelectedSubMenu", currentMenu.menuId || currentMenu.id);
          }
        }

        // localStorage에 focusedHeaderId 저장
        const selectedMenuId =
          currentMenu.menuLvl === 1
            ? currentMenu.menuId || currentMenu.id
            : currentMenu.upperMenuId || currentMenu.parentMenuId || currentMenu.parentId;

        if (selectedMenuId) {
          localStorage.setItem("focusedHeaderId", selectedMenuId);
        }
      }
    } catch (error) {
      console.error("popstate 이벤트 처리 중 오류 발생:", error);
    }
  });
});
